import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/lib/logger";
import { subMonths, format, startOfMonth, endOfMonth, eachMonthOfInterval } from "date-fns";
import { es } from "date-fns/locale";

export function GSCPerformanceChart() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const { data: settingsList } = await supabase.from('gsc_settings').select('selected_property_id').order('updated_at', { ascending: false });
        const settings = settingsList?.[0];
        
        if (!settings?.selected_property_id) {
          setLoading(false);
          return;
        }

        const propertyId = settings.selected_property_id;
        const twelveMonthsAgo = format(subMonths(new Date(), 12), 'yyyy-MM-dd');

        const { data: perfData, error } = await supabase
          .from('gsc_search_performance')
          .select('date, clicks, impressions')
          .eq('property_id', propertyId)
          .gte('date', twelveMonthsAgo)
          .order('date', { ascending: true });

        if (error) throw error;

        if (perfData && perfData.length > 0) {
          // Group by month for a cleaner chart
          const monthlyGroups = perfData.reduce((acc: any, curr: any) => {
            const month = format(new Date(curr.date), 'MMM yyyy', { locale: es });
            if (!acc[month]) acc[month] = { month, clicks: 0, impressions: 0 };
            acc[month].clicks += curr.clicks;
            acc[month].impressions += curr.impressions;
            return acc;
          }, {});

          setData(Object.values(monthlyGroups));
        }
      } catch (err) {
        logger.error("Error fetching GSC chart data:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <Skeleton className="h-80 w-full" />;
  if (data.length === 0) return null;

  return (
    <div className="chart-container">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Rendimiento SEO (Google)
          </h3>
          <p className="text-sm text-muted-foreground">
            Clicks e Impresiones mensuales
          </p>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(213, 94%, 55%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(213, 94%, 55%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.1} />
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))' }}
              itemStyle={{ fontSize: 12 }}
            />
            <Area
              type="monotone"
              dataKey="clicks"
              name="Clicks"
              stroke="hsl(213, 94%, 55%)"
              strokeWidth={2}
              fill="url(#clicksGradient)"
            />
            <Area
              type="monotone"
              dataKey="impressions"
              name="Impresiones"
              stroke="hsl(280, 94%, 55%)"
              strokeWidth={2}
              fill="transparent"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
